import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  ElementRef,
  input,
  signal,
  ViewChild,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import {
  isSendEmailActionCardPayload,
  SendEmailActionCardPayload,
} from '../../../models/action-card.types';

/** Remove HTML tags from string (e.g. <p>...</p>) for plain-text display in email body. */
function stripHtml(html: string): string {
  if (!html || typeof html !== 'string') return '';
  return html.replace(/<[^>]*>/g, '').trim();
}

/** Turn email local part into a display name (e.g. ana@synply.io -> Ana). */
function emailToDisplayName(email: string): string {
  const local = email.split('@')[0]?.trim() ?? '';
  if (!local) return email;
  return local.charAt(0).toUpperCase() + local.slice(1).toLowerCase();
}

@Component({
  selector: 'qt-ai-chat-action-card',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatButtonModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
  ],
  template: `
    @if (payload(); as p) {
      @if (isSendEmail(p)) {
        <div class="action-card action-card--email">
          <div class="action-card__header">
            <span
              class="action-card__title"
              [title]="p.card.title || 'Send email'"
              >{{ p.card.title || 'Send email' }}</span
            >
          </div>
          <div class="action-card__body">
            <div class="action-card__field">
              <label class="action-card__label">To</label>
              <div class="action-card__to-row">
                @for (r of recipientsList(); track $index) {
                  <span class="action-card__chip">
                    {{ r.name || r.email }}
                    <button
                      type="button"
                      class="action-card__chip-remove"
                      (click)="removeRecipient($index)"
                      aria-label="Remove recipient">
                      <mat-icon>close</mat-icon>
                    </button>
                  </span>
                }
                @if (hasRecipientsList()) {
                  <input
                    class="action-card__input action-card__input--add"
                    type="text"
                    [ngModel]="newRecipient()"
                    (ngModelChange)="onAddRecipientInput($event, true)"
                    (keydown.enter)="addRecipientFromInput($event)"
                    (keydown.tab)="onRecipientInputTab($event)"
                    (blur)="tryAddRecipientOnBlur()"
                    placeholder="Add recipient..." />
                } @else {
                  <input
                    class="action-card__input action-card__input--add"
                    type="text"
                    [ngModel]="toSingle()"
                    (ngModelChange)="onAddRecipientInput($event, false)"
                    (keydown.enter)="addRecipientFromInput($event)"
                    (keydown.tab)="onRecipientInputTab($event)"
                    (blur)="tryAddRecipientOnBlur()"
                    placeholder="Add recipient..." />
                }
              </div>
            </div>

            <div class="action-card__field">
              <label class="action-card__label">Subject</label>
              <input
                #subjectInput
                class="action-card__input"
                type="text"
                [ngModel]="subject()"
                (ngModelChange)="subject.set($event)"
                placeholder="Subject" />
            </div>

            <div class="action-card__field">
              <label class="action-card__label">Message</label>
              <textarea
                class="action-card__textarea"
                [ngModel]="content()"
                (ngModelChange)="content.set($event)"
                rows="6"
                placeholder="Email content"></textarea>
            </div>

            @if (handledByServer()) {
              <div class="action-card__status action-card__status--ok">
                Email sent by the assistant.
              </div>
            } @else {
              <button
                class="action-card__button action-card__button--send"
                type="button"
                (click)="sendEmail()">
                <svg
                  class="action-card__button-icon"
                  viewBox="0 -960 960 960"
                  width="24"
                  height="24"
                  fill="currentColor"
                  aria-hidden="true">
                  <path
                    d="M440-160v-487L216-423l-56-57 320-320 320 320-56 57-224-224v487h-80Z" />
                </svg>
                {{ p.card.button || 'Send Email' }}
              </button>
              @if (showMailtoHint()) {
                <p class="action-card__hint">
                  If your mail app didn't open: on Mac open the Mail app →
                  Settings → General → Default email reader → choose Outlook. On
                  Windows: Settings → Apps → Default apps → Email.
                </p>
              }
            }
          </div>
        </div>
      }
    }
  `,
  styleUrls: ['./ai-chat-action-card.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AiChatActionCardComponent {
  @ViewChild('subjectInput') subjectInputRef?: ElementRef<HTMLInputElement>;

  readonly payload = input.required<SendEmailActionCardPayload | unknown>();
  /** Previous user message text (e.g. "write email for federico@synply.io") to extract recipient emails when payload has none. */
  readonly userPromptText = input<string>('');

  protected readonly isSendEmail = isSendEmailActionCardPayload;

  /** True when the LLM/backend already sent the email (no POST, no button) */
  protected readonly handledByServer = computed(() => {
    const p = this.payload();
    if (!isSendEmailActionCardPayload(p)) return false;
    const m = (p.action?.method ?? '').toLowerCase();
    const pr = (p.action?.protocol ?? '').toLowerCase();
    return (
      m === 'server' ||
      m === 'done' ||
      m === 'llm' ||
      pr === 'server' ||
      pr === 'done' ||
      pr === 'llm'
    );
  });

  protected readonly showMailtoHint = signal(false);

  /** Editable when payload.editable is true */
  protected readonly editable = computed(() => {
    const p = this.payload();
    return isSendEmailActionCardPayload(p) && p.editable !== false;
  });

  /** Recipients from card; empty array if missing */
  protected readonly recipientsList = signal<
    Array<{ name?: string; email: string }>
  >([]);

  /** Single "To" line when card has no recipients array */
  protected readonly toSingle = signal('');

  /** Current value in the "Add recipient..." input when showing chips */
  protected readonly newRecipient = signal('');

  protected readonly subject = signal('');
  protected readonly content = signal('');

  protected readonly hasRecipientsList = computed(
    () => this.recipientsList().length > 0
  );

  protected readonly recipientEmailsLine = computed(() =>
    this.recipientsList()
      .map(r => r.email)
      .filter(Boolean)
      .join(', ')
  );

  constructor() {
    effect(() => {
      const p = this.payload();
      if (isSendEmailActionCardPayload(p)) {
        let list = p.card.recipients ?? p.card.receipients ?? [];

        // Backend may send action.params.receipients_path (e.g. "card.receipients.email") pointing to where recipients live
        if (!list.length && p.action?.params) {
          const params = p.action.params as Record<string, unknown>;
          const path =
            (params?.['receipients_path'] as string) ??
            (params?.['recipients_path'] as string);
          if (typeof path === 'string' && path.trim()) {
            const resolved = this.getRecipientsByPath(p, path.trim());
            if (resolved?.length) list = resolved;
          }
        }

        if (!list.length) {
          const card = p.card as unknown as Record<string, unknown>;
          const toStr =
            (card?.['to'] as string | undefined) ??
            (card?.['To'] as string | undefined) ??
            (card?.['recipients'] as string | undefined) ??
            (card?.['emails'] as string | undefined);
          const toArr = Array.isArray(card?.['to'])
            ? (card['to'] as string[])
            : Array.isArray(card?.['recipients'])
              ? (card['recipients'] as string[])
              : null;

          if (typeof toStr === 'string' && toStr.trim()) {
            list = toStr
              .split(/[,;\s]+/)
              .map(s => s.trim())
              .filter(Boolean)
              .map(email => ({ email }));
          } else if (toArr?.length) {
            list = toArr
              .filter((e): e is string => typeof e === 'string' && !!e.trim())
              .map(email => ({ email }));
          }
        }

        if (!list.length && p.action?.params) {
          const params = p.action.params as Record<string, unknown>;
          const rawTo =
            (params?.['to'] as string | undefined) ??
            (params?.['To'] as string | undefined) ??
            (params?.['recipients'] as string | undefined) ??
            (params?.['emails'] as string | undefined);
          if (typeof rawTo === 'string' && rawTo.trim()) {
            list = rawTo
              .split(/[,;\s]+/)
              .map(s => s.trim())
              .filter(Boolean)
              .map(email => ({ email }));
          }
        }

        if (!list.length) {
          const pl = p as unknown as Record<string, unknown>;
          const rawTo =
            (pl?.['to'] as string | undefined) ??
            (pl?.['recipients'] as string | undefined);
          if (typeof rawTo === 'string' && rawTo.trim()) {
            list = rawTo
              .split(/[,;\s]+/)
              .map(s => s.trim())
              .filter(Boolean)
              .map(email => ({ email }));
          }
        }

        if (!list.length) {
          const text = [p.card.content ?? '', p.card.subject ?? ''].join(' ');
          const emailRe = /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g;
          const emails = [...(text.match(emailRe) ?? [])];
          const unique = [...new Set(emails)];
          if (unique.length) list = unique.map(email => ({ email }));
        }

        // Last resort: extract emails from the user's prompt (e.g. "write email for federico@synply.io and ana@synply.io")
        if (!list.length) {
          const prompt = this.userPromptText()?.trim() ?? '';
          if (prompt) {
            const emailRe = /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g;
            const emails = [...(prompt.match(emailRe) ?? [])];
            const unique = [...new Set(emails)];
            if (unique.length) list = unique.map(email => ({ email }));
          }
        }

        this.recipientsList.set(list.length > 0 ? [...list] : []);
        const sub =
          p.card.subject ??
          (p.card as { emailSubject?: string }).emailSubject ??
          p.card.title ??
          '';
        this.subject.set(String(sub).trim());
        this.content.set(stripHtml(p.card.content ?? ''));
      }
    });
  }

  setRecipientEmail(index: number, value: string): void {
    this.recipientsList.update(list => {
      const next = [...list];
      if (next[index]) next[index] = { ...next[index], email: value };
      return next;
    });
  }

  removeRecipient(index: number): void {
    this.recipientsList.update(list => list.filter((_, i) => i !== index));
    this.updateGreetingInContent();
  }

  /** When user types "," or ";", add the part before as a recipient chip and keep the rest in the input. */
  onAddRecipientInput(value: string, isListMode: boolean): void {
    const sep = /[,;]/.exec(value);
    if (!sep) {
      if (isListMode) this.newRecipient.set(value);
      else this.toSingle.set(value);
      return;
    }
    const idx = sep.index;
    const first = value.slice(0, idx).trim();
    const rest = value.slice(idx + 1).trim();
    if (first) {
      this.recipientsList.update(list => [...list, { email: first }]);
      this.updateGreetingInContent();
    }
    if (isListMode) {
      this.newRecipient.set(rest);
    } else {
      this.toSingle.set('');
      this.newRecipient.set(rest);
    }
  }

  addRecipientFromInput(event: Event): void {
    event.preventDefault();
    this.commitCurrentRecipientInput();
  }

  /** When leaving the input (blur), if the current value looks like an email, add it as a chip. */
  tryAddRecipientOnBlur(): void {
    this.commitCurrentRecipientInput();
  }

  /** On Tab: create chip from current email if valid, then move focus to Subject. */
  onRecipientInputTab(event: Event): void {
    event.preventDefault();
    this.commitCurrentRecipientInput();
    setTimeout(() => this.subjectInputRef?.nativeElement?.focus(), 0);
  }

  private commitCurrentRecipientInput(): void {
    const value = this.hasRecipientsList()
      ? this.newRecipient().trim()
      : this.toSingle().trim();
    if (!value) return;
    const looksLikeEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
    if (!looksLikeEmail) return;
    this.recipientsList.update(list => [...list, { email: value }]);
    this.updateGreetingInContent();
    if (this.hasRecipientsList()) {
      this.newRecipient.set('');
    } else {
      this.toSingle.set('');
      this.newRecipient.set('');
    }
  }

  /**
   * Update the message content greeting (e.g. "Hi Federico,") to include all recipients (e.g. "Hi Federico and Ana,").
   */
  private updateGreetingInContent(): void {
    const list = this.recipientsList();
    if (list.length === 0) return;
    const names = list
      .map(r => r.name?.trim() || emailToDisplayName(r.email))
      .filter(Boolean);
    if (names.length === 0) return;
    const greetingNames = names.join(' and ');
    let body = this.content();
    const hiMatch = body.match(/^(\s*)(Hi|Dear|Hello)\s+[^,\n]+,?\s*/i);
    if (hiMatch) {
      const prefix = hiMatch[1] || '';
      const word = hiMatch[2] || 'Hi';
      body =
        prefix +
        word +
        ' ' +
        greetingNames +
        ',' +
        body.slice(hiMatch[0].length);
      this.content.set(body);
    }
  }

  /**
   * Resolve a path like "card.receipients" or "card.receipients.email" from the payload.
   * For "card.receipients.email" we resolve "card.receipients" (array) and pluck .email from each item.
   */
  private getRecipientsByPath(
    payload: SendEmailActionCardPayload,
    path: string
  ): Array<{ name?: string; email: string }> | null {
    const pathToArray = path.endsWith('.email') ? path.slice(0, -6) : path;
    const parts = pathToArray.split('.');
    let cur: unknown = payload as unknown;
    for (const part of parts) {
      if (cur == null || typeof cur !== 'object') return null;
      cur = (cur as Record<string, unknown>)[part];
    }
    if (Array.isArray(cur)) {
      return cur
        .map(item => {
          if (item != null && typeof item === 'object' && 'email' in item) {
            const e = String((item as { email?: string }).email ?? '').trim();
            return e
              ? { email: e, name: (item as { name?: string }).name }
              : null;
          }
          if (typeof item === 'string' && item.trim())
            return { email: item.trim() };
          return null;
        })
        .filter(r => r != null) as Array<{ name?: string; email: string }>;
    }
    return null;
  }

  sendEmail(): void {
    const p = this.payload();
    if (!isSendEmailActionCardPayload(p)) return;

    const emails =
      this.recipientsList().length > 0
        ? this.recipientsList()
            .map(r => r.email?.trim())
            .filter(Boolean)
        : this.toSingle()
            .split(/[,;]/)
            .map(s => s.trim())
            .filter(Boolean);
    if (emails.length === 0) return;

    const sub = this.subject().trim();
    const body = this.content().trim();

    // Open user's mail client via mailto
    const mailto =
      'mailto:' +
      emails.join(',') +
      (sub || body
        ? '?' +
          [
            sub ? `subject=${encodeURIComponent(sub)}` : '',
            body ? `body=${encodeURIComponent(body)}` : '',
          ]
            .filter(Boolean)
            .join('&')
        : '');
    window.location.href = mailto;
    this.showMailtoHint.set(true);
  }
}
